import { useState, useEffect } from 'react';
import { Brain, Pencil, SkipForward, Lock, RefreshCw, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  generateClueOptions,
  lockClue,
  skipSong,
  getClueForSong,
  generateCluesFromFacts,
  getPastLockedCluesForSong,
  TriviaClueOption,
  TriviaClue,
} from '../../services/triviaClues';

interface TriviaClvePanelProps {
  eventId: string;
  djId: string;
  songRequestId: string;
  songTitle: string;
  songArtist: string;
  songAlbumArtUrl?: string;
  onClose: () => void;
}

export default function TriviaCluePanel({
  eventId,
  djId,
  songRequestId,
  songTitle,
  songArtist,
  songAlbumArtUrl,
  onClose,
}: TriviaClvePanelProps) {
  const [clueOptions, setClueOptions] = useState<TriviaClueOption[]>([]);
  const [generating, setGenerating] = useState(false);
  const [generateCount, setGenerateCount] = useState(0);
  const [existingStatus, setExistingStatus] = useState<'none' | 'locked' | 'skipped'>('none');
  const [existingClue, setExistingClue] = useState<TriviaClue | null>(null);
  const [showCustom, setShowCustom] = useState(false);
  const [customText, setCustomText] = useState('');
  const [locking, setLocking] = useState(false);
  const [pastClues, setPastClues] = useState<TriviaClue[]>([]);
  const [pastedFacts, setPastedFacts] = useState('');
  const [generatingFromFacts, setGeneratingFromFacts] = useState(false);
  const [factsClueOptions, setFactsClueOptions] = useState<TriviaClueOption[]>([]);

  const MAX_GENERATIONS = 4;

  useEffect(() => {
    checkExisting();
    generateClues();
    getPastLockedCluesForSong(djId, songTitle, eventId)
      .then(setPastClues)
      .catch(() => {});
  }, []);

  async function checkExisting() {
    const existing = await getClueForSong(eventId, songRequestId);
    if (existing?.locked) {
      setExistingStatus('locked');
      setExistingClue(existing);
    } else if (existing?.skipped) {
      setExistingStatus('skipped');
    }
  }

  async function generateClues() {
    setGenerating(true);
    try {
      const options = await generateClueOptions(songTitle, songArtist);
      setClueOptions(prev => [...prev, ...options]);
      setGenerateCount(prev => prev + 1);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to generate clues';
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  }

  async function handleLock(clueText: string, source: 'ai' | 'custom', difficulty?: 'easy' | 'medium' | 'hard') {
    setLocking(true);
    try {
      await lockClue(eventId, djId, songRequestId, {
        title: songTitle,
        artist: songArtist,
        albumArtUrl: songAlbumArtUrl,
      }, clueText, source, difficulty);
      toast.success('Clue locked!');
      onClose();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to lock clue';
      toast.error(msg);
    } finally {
      setLocking(false);
    }
  }

  async function handleSkip() {
    try {
      await skipSong(eventId, songRequestId);
      toast.success('Song skipped for trivia');
      onClose();
    } catch (error) {
      toast.error('Failed to skip song');
    }
  }

  async function handleGenerateFromFacts() {
    if (!pastedFacts.trim()) return;
    setGeneratingFromFacts(true);
    try {
      const options = await generateCluesFromFacts(songTitle, songArtist, pastedFacts);
      setFactsClueOptions(options);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to generate clues';
      toast.error(msg);
    } finally {
      setGeneratingFromFacts(false);
    }
  }

  function openFactCheck(clueText: string) {
    const query = encodeURIComponent(`Is it true that ${clueText}? Fact check: "${songTitle}" by ${songArtist}`);
    window.open(`https://www.google.com/search?q=${query}`, '_blank', 'noopener,noreferrer');
  }

  function openResearchQuery() {
    const query = encodeURIComponent(`"${songTitle}" "${songArtist}" music trivia interesting facts history`);
    window.open(`https://www.google.com/search?q=${query}`, '_blank', 'noopener,noreferrer');
  }

  const difficultyColors: Record<string, string> = {
    easy: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    hard: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };

  const confidenceBadge = (confidence: TriviaClueOption['sourceConfidence'] | undefined) => {
    if (!confidence || confidence === 'ai-memory') {
      return <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500"><span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 inline-block" />AI memory</span>;
    }
    if (confidence === 'fact-grounded') {
      return <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400"><span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />Fact-grounded</span>;
    }
    return <span className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400"><span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />Custom source</span>;
  };

  const cachedVerificationBadge = (cv: TriviaClueOption['cachedVerification']) => {
    if (!cv) return null;
    const config = {
      true: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      false: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      uncertain: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    };
    const label = { true: 'Verified', false: 'Inaccurate', uncertain: 'Uncertain' };
    return (
      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${config[cv.verdict]}`}>
        {label[cv.verdict]}
      </span>
    );
  };

  const renderClueCard = (option: TriviaClueOption, idx: number | string) => {
    const source: 'ai' | 'custom' = option.sourceConfidence === 'custom-source' ? 'custom' : 'ai';
    return (
      <div key={String(idx)} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
        <p className="text-sm text-gray-800 dark:text-gray-200 mb-2">{option.text}</p>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${difficultyColors[option.difficulty] || ''}`}>
            {option.difficulty}
          </span>
          {confidenceBadge(option.sourceConfidence)}
          {cachedVerificationBadge(option.cachedVerification)}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleLock(option.text, source, option.difficulty)}
            disabled={locking}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Lock className="w-3 h-3" /> Use This
          </button>
          <button
            onClick={() => openFactCheck(option.text)}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <ExternalLink className="w-3 h-3" /> Fact Check
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Song Header */}
      <div className="flex items-center gap-3 pb-3 border-b border-gray-200 dark:border-gray-700">
        {songAlbumArtUrl && (
          <img src={songAlbumArtUrl} alt="" className="w-12 h-12 rounded-lg object-cover" />
        )}
        <div className="min-w-0 flex-1">
          <p className="font-medium text-gray-900 dark:text-white truncate">{songTitle}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{songArtist}</p>
          <button
            onClick={openResearchQuery}
            className="flex items-center gap-1 mt-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <ExternalLink className="w-3 h-3" />
            Search in browser
          </button>
        </div>
      </div>

      {/* Existing Status */}
      {existingStatus === 'locked' && existingClue && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <p className="text-sm font-medium text-green-700 dark:text-green-400 flex items-center gap-1.5">
            <Lock className="w-4 h-4" /> Clue already locked
          </p>
          <p className="text-sm text-green-600 dark:text-green-300 mt-1 italic">"{existingClue.clueText}"</p>
          {existingClue.timesUsed > 0 && (
            <p className="text-xs text-green-500 dark:text-green-400 mt-1">
              Used in {existingClue.timesUsed} trivia game{existingClue.timesUsed === 1 ? '' : 's'}
            </p>
          )}
          <p className="text-xs text-green-500 dark:text-green-400 mt-1">You can replace it by selecting a new clue below.</p>
        </div>
      )}
      {existingStatus === 'skipped' && (
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-sm text-gray-500 dark:text-gray-400">Previously skipped. You can still lock a clue.</p>
        </div>
      )}

      {/* AI Clue Options */}
      {generating ? (
        <div className="flex items-center justify-center py-8 gap-2 text-gray-500 dark:text-gray-400">
          <Brain className="w-5 h-5 animate-pulse" />
          <span>Generating clues...</span>
        </div>
      ) : (
        <div className="space-y-3">
          {clueOptions.map((option, idx) => renderClueCard(option, idx))}
        </div>
      )}

      {/* Generate More / Custom / Skip */}
      <div className="flex items-center gap-2 flex-wrap">
        {generateCount < MAX_GENERATIONS ? (
          <button
            onClick={generateClues}
            disabled={generating}
            className="flex items-center gap-1.5 text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
            Generate More ({generateCount}/{MAX_GENERATIONS})
          </button>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-500 px-1">Max clues reached — try writing your own.</span>
        )}
        <button
          onClick={() => setShowCustom(!showCustom)}
          className="flex items-center gap-1.5 text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <Pencil className="w-4 h-4" /> Write My Own
        </button>
        <button
          onClick={handleSkip}
          className="flex items-center gap-1.5 text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 ml-auto"
        >
          <SkipForward className="w-4 h-4" /> Skip
        </button>
      </div>

      {/* Custom Clue + Paste Facts */}
      {showCustom && (
        <div className="space-y-4">
          {/* Paste-your-own-facts */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Paste facts from the web</p>
            <textarea
              value={pastedFacts}
              onChange={(e) => setPastedFacts(e.target.value)}
              placeholder="Paste text from Wikipedia, AllMusic, Google, etc..."
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm resize-none"
              rows={4}
            />
            <button
              onClick={handleGenerateFromFacts}
              disabled={!pastedFacts.trim() || generatingFromFacts}
              className="flex items-center gap-1.5 text-sm px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              <Brain className={`w-4 h-4 ${generatingFromFacts ? 'animate-pulse' : ''}`} />
              {generatingFromFacts ? 'Generating...' : 'Generate from these facts'}
            </button>
            {factsClueOptions.length > 0 && (
              <div className="space-y-2 pt-1">
                <p className="text-xs text-gray-500 dark:text-gray-400">From your pasted facts:</p>
                {factsClueOptions.map((option, idx) => renderClueCard(option, `facts-${idx}`))}
              </div>
            )}
          </div>

          {/* Write your own */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Write your own clue</p>
            <textarea
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="Write a trivia clue that describes this song without naming it..."
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm resize-none"
              rows={3}
            />
            <button
              onClick={() => handleLock(customText, 'custom')}
              disabled={!customText.trim() || locking}
              className="flex items-center gap-1 text-sm px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              <Lock className="w-4 h-4" /> Lock Custom Clue
            </button>
          </div>
        </div>
      )}

      {/* Previously used clues from other events */}
      {pastClues.length > 0 && (
        <div className="pt-2">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">Previously used at other events</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          </div>
          <div className="space-y-2">
            {pastClues.map((clue) => (
              <div key={clue.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800/50">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-1 italic">"{clue.clueText}"</p>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {clue.difficulty && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${difficultyColors[clue.difficulty] || ''}`}>
                        {clue.difficulty}
                      </span>
                    )}
                    {clue.timesUsed > 0 && (
                      <span className="text-xs text-gray-400 dark:text-gray-500">Used {clue.timesUsed}×</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleLock(clue.clueText, 'ai', clue.difficulty)}
                    disabled={locking}
                    className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    <Lock className="w-3 h-3" /> Use This
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
